import { MapPin, Phone, Clock, ArrowUpRight } from "lucide-react";

export default function LocationHours({ settings }) {
  const address = settings?.address || "715 Bennetts Mills Rd, Jackson Township, NJ 08527";
  const phone = settings?.phone || "(732) 928-9115";
  const hours = settings?.hours || "Mon–Sun: 11:00 AM – 9:00 PM";
  const orderUrl = settings?.order_url || "#";
  const telHref = `tel:${phone.replace(/[^0-9+]/g, "")}`;
  const mapsQuery = encodeURIComponent(address);

  return (
    <section
      id="visit"
      data-testid="visit-section"
      className="py-24 md:py-32 bg-[var(--charcoal)] text-[var(--dough)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
        <div>
          <span className="section-rule" />
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold leading-[1.05]">
            Come <span className="italic text-[#F5D7A6]">visit</span> us.
          </h2>
          <p className="mt-4 text-[var(--dough)]/70 max-w-md text-base md:text-lg">
            Dine in, pick up, or have it delivered right to your door.
          </p>

          <div className="mt-10 space-y-6">
            <InfoRow icon={<MapPin size={18} />} title="Address">
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visit-address-link"
                className="hover:text-[#F5D7A6] transition-colors"
              >
                {address}
              </a>
            </InfoRow>

            <InfoRow icon={<Phone size={18} />} title="Phone">
              <a
                href={telHref}
                data-testid="visit-call-link"
                className="hover:text-[#F5D7A6] transition-colors"
              >
                {phone}
              </a>
            </InfoRow>

            <InfoRow icon={<Clock size={18} />} title="Hours">
              {hours}
            </InfoRow>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={orderUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-order-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--terracotta)] text-white font-semibold hover:bg-[var(--terracotta-hover)] transition-all hover:-translate-y-0.5"
            >
              Order Online <ArrowUpRight size={16} />
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${mapsQuery}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-directions-btn"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white font-medium hover:bg-white/10 transition-colors"
            >
              Get directions
            </a>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden border border-white/10">
          <iframe
            title="Emilio's Pizza location map"
            src={`https://maps.google.com/maps?q=${mapsQuery}&z=15&output=embed`}
            width="100%"
            height="480"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="visit-map-iframe"
          />
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon, title, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="mt-1 h-10 w-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[#F5D7A6]">
        {icon}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--dough)]/60">
          {title}
        </div>
        <div className="mt-1 text-base md:text-lg text-[var(--dough)]">{children}</div>
      </div>
    </div>
  );
}
